import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Trash2, AlertTriangle } from "lucide-react";
import { sendLeadToCrm } from "@/lib/crm.functions";

export const Route = createFileRoute("/_authenticated/admin/leads")({
  
  head: () => ({ meta: [{ title: "Admin — Leads | Mine Class" }, { name: "description", content: "Panel de administración de Leads — acceso restringido a Mine Class." }, { name: "robots", content: "noindex, nofollow" }] }),
  component: LeadsAdmin,
});

type LeadStatus = "new" | "contacted" | "qualified" | "won" | "lost";
type LeadSource = "form" | "meeting";

type Lead = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  company: string | null;
  job_title: string | null;
  country: string | null;
  employees: number | null;
  interest: string | null;
  message: string | null;
  status: LeadStatus;
  source: LeadSource;
  created_at: string;
  crm_synced: boolean | null;
};

type Attribution = {
  lead_id: string;
  first_utm_source: string | null;
  first_utm_medium: string | null;
  first_utm_campaign: string | null;
  first_utm_term: string | null;
  first_utm_content: string | null;
  first_referrer: string | null;
  first_landing_page: string | null;
  first_seen_at: string | null;
  last_utm_source: string | null;
  last_utm_medium: string | null;
  last_utm_campaign: string | null;
  last_utm_term: string | null;
  last_utm_content: string | null;
  last_referrer: string | null;
  last_landing_page: string | null;
  last_seen_at: string | null;
};

const STATUS_LABEL: Record<LeadStatus, string> = {
  new: "Nuevo",
  contacted: "Contactado",
  qualified: "Calificado",
  won: "Ganado",
  lost: "Perdido",
};

const SOURCE_LABEL: Record<LeadSource, string> = {
  form: "Formulario",
  meeting: "Reunión",
};

function LeadsAdmin() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [attrs, setAttrs] = useState<Record<string, Attribution>>({});
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [source, setSource] = useState<"all" | LeadSource>("all");
  const [status, setStatus] = useState<"all" | LeadStatus>("all");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState("");
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from("leads")
      .select("id,first_name,last_name,email,phone,company,job_title,country,employees,interest,message,status,source,created_at,crm_synced")
      .order("created_at", { ascending: false });
    setLeads((data ?? []) as Lead[]);
    const ids = (data ?? []).map((l: { id: string }) => l.id);
    if (ids.length) {
      const { data: a } = await supabase
        .from("lead_attribution")
        .select("*")
        .in("lead_id", ids);
      const map: Record<string, Attribution> = {};
      (a ?? []).forEach((r: Attribution) => { map[r.lead_id] = r; });
      setAttrs(map);
    }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (source !== "all" && l.source !== source) return false;
      if (status !== "all" && l.status !== status) return false;
      if (from && new Date(l.created_at) < new Date(from)) return false;
      if (to && new Date(l.created_at) > new Date(to + "T23:59:59")) return false;
      if (q) {
        const s = q.toLowerCase();
        const hay = `${l.first_name} ${l.last_name} ${l.email} ${l.company ?? ""} ${l.interest ?? ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [leads, source, status, from, to, q]);

  async function updateStatus(id: string, next: LeadStatus) {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status: next } : l)));
    await supabase.from("leads").update({ status: next }).eq("id", id);
  }

  async function deleteLead(id: string) {
    setDeletingId(id);
    const { error } = await supabase.from("leads").delete().eq("id", id);
    if (!error) {
      setLeads((prev) => prev.filter((l) => l.id !== id));
      setAttrs((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      if (expanded === id) setExpanded(null);
    }
    setDeletingId(null);
    setConfirmId(null);
  }

  async function sendPending() {
    const pending = leads.filter((l) => l.crm_synced !== true);
    if (!pending.length) {
      setBulkResult("No hay contactos pendientes.");
      return;
    }
    setBulkRunning(true);
    setBulkResult(null);
    let ok = 0;
    let fail = 0;
    for (let i = 0; i < pending.length; i++) {
      const l = pending[i];
      setBulkProgress(`Enviando ${i + 1} de ${pending.length}…`);
      const a = attrs[l.id];
      const fecha = new Date(l.created_at).toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" });
      const nota = `[Contacto original: ${fecha}]`;
      const mensaje = (l.message ? `${l.message}\n\n${nota}` : nota).slice(0, 5000);
      let success = false;
      try {
        const r = await sendLeadToCrm({
          data: {
            lead_id: l.id,
            nombre: `${l.first_name} ${l.last_name}`.trim().slice(0, 200),
            email: l.email,
            telefono: (l.phone ?? "").slice(0, 50),
            empresa: (l.company ?? "").slice(0, 200),
            cargo: (l.job_title ?? "").slice(0, 200),
            interes: (l.interest ?? "").slice(0, 200),
            mensaje,
            utm_source: (a?.last_utm_source ?? a?.first_utm_source ?? "").slice(0, 200),
            utm_medium: (a?.last_utm_medium ?? a?.first_utm_medium ?? "").slice(0, 200),
            utm_campaign: (a?.last_utm_campaign ?? a?.first_utm_campaign ?? "").slice(0, 200),
            pagina_origen: (a?.last_landing_page ?? a?.first_landing_page ?? "").slice(0, 2000),
          },
        });
        success = r.ok;
      } catch {
        success = false;
      }
      if (success) ok++; else fail++;
      setLeads((prev) => prev.map((x) => (x.id === l.id ? { ...x, crm_synced: success } : x)));
      if (i < pending.length - 1) await new Promise((res) => setTimeout(res, 800));
    }
    setBulkProgress("");
    setBulkRunning(false);
    setBulkResult(`Listo: ${ok} enviados correctamente, ${fail} fallaron.`);
  }

  function exportCSV() {
    const cols = [
      "created_at","source","status","first_name","last_name","email","phone","company","job_title","country","employees","interest","message","crm_synced",
      "first_utm_source","first_utm_medium","first_utm_campaign","first_utm_term","first_utm_content","first_referrer","first_landing_page","first_seen_at",
      "last_utm_source","last_utm_medium","last_utm_campaign","last_utm_term","last_utm_content","last_referrer","last_landing_page","last_seen_at",
    ];
    const esc = (v: unknown) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = filtered.map((l) => {
      const a = attrs[l.id] ?? ({} as Partial<Attribution>);
      return cols.map((c) => {
        if (c in l) return esc((l as unknown as Record<string, unknown>)[c]);
        return esc((a as unknown as Record<string, unknown>)[c]);
      }).join(",");
    });
    const csv = [cols.join(","), ...rows].join("\n");
    const blob = new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-bold text-lg">Leads ({filtered.length}/{leads.length})</h2>
          <p className="text-xs text-[var(--color-on-surface-variant)]">
            Formularios y solicitudes de reunión. Cambia el estado y expande cada lead para ver la atribución UTM.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={sendPending}
            disabled={bulkRunning || loading}
            className="px-4 py-2 bg-[var(--color-brand-purple)] text-white font-bold disabled:opacity-50"
          >
            {bulkRunning ? bulkProgress || "Enviando…" : `Enviar pendientes al CRM (${leads.filter((l) => l.crm_synced !== true).length})`}
          </button>
          <button
            onClick={exportCSV}
            disabled={filtered.length === 0}
            className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold disabled:opacity-50"
          >
            Descargar CSV
          </button>
        </div>
      </div>
      {bulkResult && (
        <div className="border border-[var(--color-outline-variant)] bg-white px-4 py-3 text-sm font-bold flex justify-between">
          <span>{bulkResult}</span>
          <button onClick={() => setBulkResult(null)} aria-label="Cerrar">×</button>
        </div>
      )}

      <div className="grid md:grid-cols-5 gap-3 bg-white border border-[var(--color-outline-variant)] p-4">
        <Field label="Desde">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="w-full border border-[var(--color-outline-variant)] px-2 py-1.5 text-sm" />
        </Field>
        <Field label="Hasta">
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="w-full border border-[var(--color-outline-variant)] px-2 py-1.5 text-sm" />
        </Field>
        <Field label="Tipo">
          <select value={source} onChange={(e) => setSource(e.target.value as typeof source)}
            className="w-full border border-[var(--color-outline-variant)] px-2 py-1.5 text-sm bg-white">
            <option value="all">Todos</option>
            <option value="form">Formulario</option>
            <option value="meeting">Reunión</option>
          </select>
        </Field>
        <Field label="Estado">
          <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}
            className="w-full border border-[var(--color-outline-variant)] px-2 py-1.5 text-sm bg-white">
            <option value="all">Todos</option>
            {(Object.keys(STATUS_LABEL) as LeadStatus[]).map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
        </Field>
        <Field label="Buscar">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="nombre, email, empresa…"
            className="w-full border border-[var(--color-outline-variant)] px-2 py-1.5 text-sm" />
        </Field>
      </div>

      <div className="bg-white border border-[var(--color-outline-variant)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--color-surface-container-low)] text-left">
            <tr>
              <th className="px-3 py-3 w-8"></th>
              <th className="px-3 py-3">Fecha</th>
              <th className="px-3 py-3">Tipo</th>
              <th className="px-3 py-3">Nombre</th>
              <th className="px-3 py-3">Empresa</th>
              <th className="px-3 py-3">Correo</th>
              <th className="px-3 py-3">Interés</th>
              <th className="px-3 py-3">Origen</th>
              <th className="px-3 py-3">Enviado al CRM</th>
              <th className="px-3 py-3">Estado</th>
              <th className="px-3 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-[var(--color-on-surface-variant)]">Cargando…</td></tr>
            )}
            {!loading && filtered.map((l) => {
              const a = attrs[l.id];
              const isOpen = expanded === l.id;
              const origin = a?.first_utm_source
                ? `${a.first_utm_source}${a.first_utm_medium ? " / " + a.first_utm_medium : ""}${a.first_utm_campaign ? " · " + a.first_utm_campaign : ""}`
                : a?.first_referrer
                  ? new URL(a.first_referrer).hostname
                  : "directo";
              return (
                <Fragment key={l.id}>
                  <tr key={l.id} className="border-t border-[var(--color-outline-variant)] align-top">
                    <td className="px-3 py-3">
                      <button onClick={() => setExpanded(isOpen ? null : l.id)} className="text-xs">
                        {isOpen ? "▾" : "▸"}
                      </button>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">{new Date(l.created_at).toLocaleString("es-CL")}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-block text-xs font-bold px-2 py-0.5 ${l.source === "meeting" ? "bg-[var(--color-brand-purple)] text-white" : "bg-[var(--color-brand-coral)] text-white"}`}>
                        {SOURCE_LABEL[l.source]}
                      </span>
                    </td>
                    <td className="px-3 py-3">{l.first_name} {l.last_name}</td>
                    <td className="px-3 py-3">{l.company ?? "—"}</td>
                    <td className="px-3 py-3"><a href={`mailto:${l.email}`} className="underline">{l.email}</a></td>
                    <td className="px-3 py-3">{l.interest ?? "—"}</td>
                    <td className="px-3 py-3 text-xs">{origin}</td>
                    <td className="px-3 py-3 text-xs font-bold">{l.crm_synced === true ? "Sí" : l.crm_synced === false ? "No" : "—"}</td>
                    <td className="px-3 py-3">
                      <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value as LeadStatus)}
                        className="border border-[var(--color-outline-variant)] px-2 py-1 text-xs bg-white">
                        {(Object.keys(STATUS_LABEL) as LeadStatus[]).map((s) => (
                          <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button
                        onClick={() => setConfirmId(l.id)}
                        disabled={deletingId === l.id}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-bold bg-[var(--color-destructive)] text-[var(--color-destructive-foreground)] hover:opacity-90 disabled:opacity-50"
                        title="Eliminar lead"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Eliminar
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr key={l.id + "-x"} className="bg-[var(--color-surface-container-low)]">
                      <td></td>
                      <td colSpan={10} className="px-4 py-4">
                        <div className="grid md:grid-cols-3 gap-4 text-xs">
                          <div>
                            <h4 className="font-bold uppercase tracking-wider text-[var(--color-brand-purple)] mb-1">Contacto</h4>
                            <p>Teléfono: {l.phone ?? "—"}</p>
                            <p>Cargo: {l.job_title ?? "—"}</p>
                            <p>País: {l.country ?? "—"}</p>
                            <p>Empleados: {l.employees ?? "—"}</p>
                            {l.message && <p className="mt-2">Mensaje: <em>{l.message}</em></p>}
                          </div>
                          <div>
                            <h4 className="font-bold uppercase tracking-wider text-[var(--color-brand-purple)] mb-1">First touch</h4>
                            {a ? (
                              <>
                                <p>source: {a.first_utm_source ?? "—"}</p>
                                <p>medium: {a.first_utm_medium ?? "—"}</p>
                                <p>campaign: {a.first_utm_campaign ?? "—"}</p>
                                <p>term: {a.first_utm_term ?? "—"}</p>
                                <p>content: {a.first_utm_content ?? "—"}</p>
                                <p className="truncate">referrer: {a.first_referrer ?? "—"}</p>
                                <p className="truncate">landing: {a.first_landing_page ?? "—"}</p>
                              </>
                            ) : <p>Sin atribución</p>}
                          </div>
                          <div>
                            <h4 className="font-bold uppercase tracking-wider text-[var(--color-brand-purple)] mb-1">Last touch</h4>
                            {a ? (
                              <>
                                <p>source: {a.last_utm_source ?? "—"}</p>
                                <p>medium: {a.last_utm_medium ?? "—"}</p>
                                <p>campaign: {a.last_utm_campaign ?? "—"}</p>
                                <p>term: {a.last_utm_term ?? "—"}</p>
                                <p>content: {a.last_utm_content ?? "—"}</p>
                                <p className="truncate">referrer: {a.last_referrer ?? "—"}</p>
                                <p className="truncate">landing: {a.last_landing_page ?? "—"}</p>
                              </>
                            ) : <p>Sin atribución</p>}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-[var(--color-on-surface-variant)]">Sin resultados.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {confirmId && (
        <DeleteConfirmModal
          lead={leads.find((l) => l.id === confirmId)!}
          onCancel={() => setConfirmId(null)}
          onConfirm={() => deleteLead(confirmId)}
          deleting={deletingId === confirmId}
        />
      )}
    </div>
  );
}

function DeleteConfirmModal({
  lead,
  onCancel,
  onConfirm,
  deleting,
}: {
  lead: Lead;
  onCancel: () => void;
  onConfirm: () => void;
  deleting: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md bg-[var(--color-background)] text-[var(--color-foreground)] p-6 shadow-lg">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-[var(--color-destructive)]/10 p-2 text-[var(--color-destructive)]">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-lg">¿Eliminar este lead?</h3>
            <p className="text-sm text-[var(--color-on-surface-variant)] mt-1">
              Se eliminará permanentemente a <strong>{lead.first_name} {lead.last_name}</strong> ({lead.email}).
              Esta acción no se puede deshacer.
            </p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="px-4 py-2 text-sm font-bold border border-[var(--color-outline-variant)] hover:bg-[var(--color-surface-container-low)] disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="px-4 py-2 text-sm font-bold bg-[var(--color-destructive)] text-[var(--color-destructive-foreground)] hover:opacity-90 disabled:opacity-50"
          >
            {deleting ? "Eliminando…" : "Sí, eliminar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-on-surface-variant)] mb-1">{label}</div>
      {children}
    </label>
  );
}
